import React from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/lato/300.css";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Auth0Provider } from "@auth0/auth0-react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AppProvider } from "providers/AppProvider";
import "./i18n";
import AuthView from "./views/AuthView";
import AppLoading from "./views/AppLoading";
import AxiosInterceptor from "api/AxiosInterceptor";
import { Navigate } from "react-router-dom";
import Routes from "./Routes";
import LoginView from "./views/LoginView";
import { createSeasonRedirectLoader } from "./loaders";
import { ThemeProvider } from "lib";
import { settingsStoreId } from "views/SettingsView/useSettingsStore";
import RouteErrorElement from "components/RouteErrorElement";
import "./app.scss";

// Helper to convert default export to Component for React Router lazy
const lazyComponent = (
  importFn: () => Promise<{ default: React.ComponentType }>
) => importFn().then((m) => ({ Component: m.default }));

// Read theme from localStorage synchronously to prevent flash of wrong theme
const getInitialTheme = (): "dark" | "light" => {
  try {
    const settings = localStorage.getItem(settingsStoreId);
    if (settings) {
      const parsed = JSON.parse(settings);
      return parsed.theme === "light" ? "light" : "dark";
    }
  } catch {
    // Ignore parse errors
  }
  return "dark"; // Default to dark
};

const initialTheme = getInitialTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // don't retry on dev (default is 3)
      retry: process.env.NODE_ENV !== "development" ? 3 : false,
    },
  },
});
window.REACT_APP_API_ORIGIN = process.env.REACT_APP_API_ORIGIN;
window.REACT_APP_TILESERVER_ORIGIN = process.env.REACT_APP_TILESERVER_ORIGIN;
window.REACT_APP_MAPBOX_API_TOKEN = process.env.REACT_APP_MAPBOX_API_TOKEN;

const domain =
  typeof process.env.REACT_APP_AUTH0_DOMAIN !== "undefined"
    ? process.env.REACT_APP_AUTH0_DOMAIN
    : null;
const clientId =
  typeof process.env.REACT_APP_AUTH0_CLIENT_ID !== "undefined"
    ? process.env.REACT_APP_AUTH0_CLIENT_ID
    : null;
const audience =
  typeof process.env.REACT_APP_AUTH0_AUDIENCE !== "undefined"
    ? process.env.REACT_APP_AUTH0_AUDIENCE
    : null;

// Create loader with access to queryClient
const seasonRedirectLoader = createSeasonRedirectLoader(queryClient);

// Create data router with proper route structure
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AppProvider>
        <Routes />
      </AppProvider>
    ),
    errorElement: <RouteErrorElement />,
    // No SSR - provide fallback to silence hydration warning
    HydrateFallback: () => (
      <AuthView>
        <AppLoading />
      </AuthView>
    ),
    children: [
      {
        index: true,
        element: <LoginView />,
      },
      {
        // /:org - redirect to /:org/:season via loader or component
        path: ":org",
        loader: seasonRedirectLoader,
        // Use React Router's lazy loading to prevent loading app bundle on login page
        lazy: async () => {
          const { default: App } = await import("./app");
          return { Component: App };
        },
      },
      {
        // /:org/:season/* - main app routes
        path: ":org/:season",
        // Use React Router's lazy loading to prevent loading app bundle on login page
        lazy: async () => {
          const { default: App } = await import("./app");
          return { Component: App };
        },
        children: [
          {
            index: true,
            lazy: () =>
              lazyComponent(
                () => import("./views/DashboardView/DashboardView")
              ),
          },
          {
            path: "verity",
            lazy: () =>
              lazyComponent(
                () => import("./components/Verity/VerityModuleCards")
              ),
          },
          {
            path: "inventory/*",
            lazy: () => lazyComponent(() => import("./views/InventoryView")),
          },
          {
            path: "imagery/*",
            lazy: () => lazyComponent(() => import("./views/ImageryView")),
          },
          {
            path: "insights/*",
            lazy: () => lazyComponent(() => import("./views/InsightsView")),
          },
          {
            path: "settings/*",
            lazy: () => lazyComponent(() => import("./views/SettingsView")),
          },
          {
            path: "*",
            element: <Navigate to="." replace />,
          },
        ],
      },
    ],
  },
]);

const root = createRoot(document.getElementById("root")!);
root.render(
  <ThemeProvider mode={initialTheme}>
    <React.Suspense
      fallback={
        <AuthView>
          <AppLoading />
        </AuthView>
      }
    >
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience,
        }}
        // Auth0 always shows a consent screen on localhost callbacks, and silent
        // auth (prompt=none) can't display it, so in-memory tokens lost on reload
        // mean a consent prompt every time. Persist them in local dev only;
        // deployed builds keep the XSS-safer memory cache.
        cacheLocation={
          process.env.NODE_ENV === "development" ? "localstorage" : "memory"
        }
      >
        <QueryClientProvider client={queryClient}>
          <AxiosInterceptor />
          <RouterProvider router={router} />
        </QueryClientProvider>
      </Auth0Provider>
    </React.Suspense>
  </ThemeProvider>
);
